import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  createOrgApi,
  deleteOrgApi,
  getOrgApi,
  updateOrgApi,
  getUserOrganizationsApi,
  getOrgPictureApi,
  uploadOrgPictureApi,
  getOrgThemeApi,
  updateOrgThemeApi,
  transferOrgOwnershipApi,
  type CreateOrgRequestContent,
  type UpdateOrgRequestContent,
  type UpdateOrgThemeRequestContent,
  type TransferOrgOwnershipRequestContent,
  type OrgTheme
} from '../../organization';
import { rawAxios } from '../../common';

/**
 * [EQIQ-018:organizations] Organization endpoints return generic 500s on
 * validation failures. Should return 400/403/404 with descriptive messages:
 * - Invalid organization ID
 * - User not authorized
 * - Resource not found
 * - Validation failures
 */

/**
 * Grouped test component for all Organization endpoints
 */
const TestOrganizations: React.FC = () => {
  // Create Org state
  const [createOrgName, setCreateOrgName] = useState('');
  const [createOrgType, setCreateOrgType] = useState('BUSINESS');
  const [createOrgBillingEmail, setCreateOrgBillingEmail] = useState('');
  const [createOrgResponse, setCreateOrgResponse] = useState<any>(null);
  const [createOrgLoading, setCreateOrgLoading] = useState(false);
  const [createOrgError, setCreateOrgError] = useState<string | null>(null);

  // Get Org state
  const [getOrgId, setGetOrgId] = useState('');
  const [getOrgResponse, setGetOrgResponse] = useState<any>(null);
  const [getOrgLoading, setGetOrgLoading] = useState(false);
  const [getOrgError, setGetOrgError] = useState<string | null>(null);

  // Update Org state
  const [updateOrgId, setUpdateOrgId] = useState('');
  const [updateOrgName, setUpdateOrgName] = useState('');
  const [updateOrgResponse, setUpdateOrgResponse] = useState<any>(null);
  const [updateOrgLoading, setUpdateOrgLoading] = useState(false);
  const [updateOrgError, setUpdateOrgError] = useState<string | null>(null);

  // Delete Org state
  const [deleteOrgId, setDeleteOrgId] = useState('');
  const [deleteOrgResponse, setDeleteOrgResponse] = useState<any>(null);
  const [deleteOrgLoading, setDeleteOrgLoading] = useState(false);
  const [deleteOrgError, setDeleteOrgError] = useState<string | null>(null);

  // List User Orgs state
  const [listUserOrgsResponse, setListUserOrgsResponse] = useState<any>(null);
  const [listUserOrgsLoading, setListUserOrgsLoading] = useState(false);
  const [listUserOrgsError, setListUserOrgsError] = useState<string | null>(null);

  // Transfer Ownership state
  const [transferOrgId, setTransferOrgId] = useState('');
  const [transferNewOwnerId, setTransferNewOwnerId] = useState('');
  const [transferResponse, setTransferResponse] = useState<any>(null);
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferError, setTransferError] = useState<string | null>(null);

  // Upload Org Picture state
  const [uploadPictureOrgId, setUploadPictureOrgId] = useState('');
  const [uploadPictureFile, setUploadPictureFile] = useState<File | null>(null);
  const [uploadPictureResponse, setUploadPictureResponse] = useState<any>(null);
  const [uploadPictureLoading, setUploadPictureLoading] = useState(false);
  const [uploadPictureError, setUploadPictureError] = useState<string | null>(null);

  // Get Org Picture state
  const [getPictureOrgId, setGetPictureOrgId] = useState('');
  const [getPictureResponse, setGetPictureResponse] = useState<any>(null);
  const [getPictureLoading, setGetPictureLoading] = useState(false);
  const [getPictureError, setGetPictureError] = useState<string | null>(null);

  // Get Org Theme state
  const [getThemeOrgId, setGetThemeOrgId] = useState('');
  const [getThemeResponse, setGetThemeResponse] = useState<any>(null);
  const [getThemeLoading, setGetThemeLoading] = useState(false);
  const [getThemeError, setGetThemeError] = useState<string | null>(null);

  // Update Org Theme state
  const [updateThemeOrgId, setUpdateThemeOrgId] = useState('');
  const [updateThemePrimaryColor, setUpdateThemePrimaryColor] = useState('');
  const [updateThemeSecondaryColor, setUpdateThemeSecondaryColor] = useState('');
  const [updateThemeAccentColor, setUpdateThemeAccentColor] = useState('');
  const [updateThemeResponse, setUpdateThemeResponse] = useState<any>(null);
  const [updateThemeLoading, setUpdateThemeLoading] = useState(false);
  const [updateThemeError, setUpdateThemeError] = useState<string | null>(null);

  // Handlers
  const handleCreateOrg = async () => {
    if (!createOrgName.trim() || !createOrgBillingEmail.trim()) {
      setCreateOrgError('Please enter organization name and billing email');
      return;
    }

    setCreateOrgLoading(true);
    setCreateOrgError(null);

    try {
      const request: CreateOrgRequestContent = {
        name: createOrgName.trim(),
        type: createOrgType as any,
        billingEmail: createOrgBillingEmail.trim()
      };
      const result = await createOrgApi(request);
      setCreateOrgResponse(result);
    } catch (err) {
      setCreateOrgError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setCreateOrgLoading(false);
    }
  };

  const handleGetOrg = async () => {
    if (!getOrgId.trim()) {
      setGetOrgError('Please enter an organization ID');
      return;
    }

    setGetOrgLoading(true);
    setGetOrgError(null);

    try {
      const result = await getOrgApi({ orgId: getOrgId.trim() });
      setGetOrgResponse(result);
    } catch (err) {
      setGetOrgError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setGetOrgLoading(false);
    }
  };

  const handleUpdateOrg = async () => {
    if (!updateOrgId.trim() || !updateOrgName.trim()) {
      setUpdateOrgError('Please enter both organization ID and name');
      return;
    }

    setUpdateOrgLoading(true);
    setUpdateOrgError(null);

    try {
      const request: UpdateOrgRequestContent = {
        orgId: updateOrgId.trim(),
        name: updateOrgName.trim()
      };
      const result = await updateOrgApi(request);
      setUpdateOrgResponse(result);
    } catch (err) {
      setUpdateOrgError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setUpdateOrgLoading(false);
    }
  };

  const handleDeleteOrg = async () => {
    if (!deleteOrgId.trim()) {
      setDeleteOrgError('Please enter an organization ID');
      return;
    }

    setDeleteOrgLoading(true);
    setDeleteOrgError(null);

    try {
      const result = await deleteOrgApi({ orgId: deleteOrgId.trim() });
      setDeleteOrgResponse(result);
    } catch (err) {
      setDeleteOrgError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setDeleteOrgLoading(false);
    }
  };

  const handleListUserOrgs = async () => {
    setListUserOrgsLoading(true);
    setListUserOrgsError(null);

    try {
      const result = await getUserOrganizationsApi();
      setListUserOrgsResponse(result);
    } catch (err) {
      setListUserOrgsError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setListUserOrgsLoading(false);
    }
  };

  const handleTransferOwnership = async () => {
    if (!transferOrgId.trim() || !transferNewOwnerId.trim()) {
      setTransferError('Please enter both organization ID and new owner ID');
      return;
    }

    setTransferLoading(true);
    setTransferError(null);

    try {
      // [EQIQ-018:transfer-ownership] transferOwnership returns 500 when target
      // user is not a member; should return 400 with a specific message.
      const request: TransferOrgOwnershipRequestContent = {
        orgId: transferOrgId.trim(),
        newOwnerId: transferNewOwnerId.trim()
      };
      const result = await transferOrgOwnershipApi(request);
      setTransferResponse(result);
    } catch (err) {
      setTransferError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setTransferLoading(false);
    }
  };

  const handleUploadOrgPicture = async () => {
    if (!uploadPictureOrgId.trim() || !uploadPictureFile) {
      setUploadPictureError('Please enter organization ID and select a file');
      return;
    }

    setUploadPictureLoading(true);
    setUploadPictureError(null);

    try {
      // Get presigned URL
      const urlInfo = await uploadOrgPictureApi(uploadPictureOrgId.trim());

      // Upload to S3 using FormData
      const formData = new FormData();
      Object.entries(urlInfo.urlInfo.fields as Record<string, string>).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append('file', uploadPictureFile);

      await rawAxios.post(urlInfo.urlInfo.url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setUploadPictureResponse({ message: 'Organization picture uploaded successfully', urlInfo });
    } catch (err) {
      setUploadPictureError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploadPictureLoading(false);
    }
  };

  const handleGetOrgPicture = async () => {
    if (!getPictureOrgId.trim()) {
      setGetPictureError('Please enter an organization ID');
      return;
    }

    setGetPictureLoading(true);
    setGetPictureError(null);

    try {
      const result = await getOrgPictureApi({ orgId: getPictureOrgId.trim() });
      setGetPictureResponse(result);
    } catch (err) {
      setGetPictureError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setGetPictureLoading(false);
    }
  };

  const handleGetOrgTheme = async () => {
    if (!getThemeOrgId.trim()) {
      setGetThemeError('Please enter an organization ID');
      return;
    }

    setGetThemeLoading(true);
    setGetThemeError(null);

    try {
      const result = await getOrgThemeApi({ orgId: getThemeOrgId.trim() });
      setGetThemeResponse(result);
    } catch (err) {
      setGetThemeError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setGetThemeLoading(false);
    }
  };

  const handleUpdateOrgTheme = async () => {
    if (!updateThemeOrgId.trim()) {
      setUpdateThemeError('Please enter an organization ID');
      return;
    }

    setUpdateThemeLoading(true);
    setUpdateThemeError(null);

    try {
      const theme: OrgTheme = {};
      if (updateThemePrimaryColor.trim()) theme.primaryColor = updateThemePrimaryColor.trim();
      if (updateThemeSecondaryColor.trim()) theme.secondaryColor = updateThemeSecondaryColor.trim();
      if (updateThemeAccentColor.trim()) theme.accentColor = updateThemeAccentColor.trim();

      const request: UpdateOrgThemeRequestContent = {
        orgId: updateThemeOrgId.trim(),
        theme
      };
      const result = await updateOrgThemeApi(request);
      setUpdateThemeResponse(result);
    } catch (err) {
      setUpdateThemeError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setUpdateThemeLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom sx={{ color: '#fff' }}>
        Organization APIs
      </Typography>

      {/* Create Organization */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Create Organization</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Organization Name"
              value={createOrgName}
              onChange={(e) => setCreateOrgName(e.target.value)}
              fullWidth
            />
            <TextField
              label="Organization Type"
              value={createOrgType}
              onChange={(e) => setCreateOrgType(e.target.value)}
              fullWidth
              placeholder="BUSINESS, PERSONAL, etc."
            />
            <TextField
              label="Billing Email"
              value={createOrgBillingEmail}
              onChange={(e) => setCreateOrgBillingEmail(e.target.value)}
              fullWidth
              type="email"
              placeholder="billing@example.com"
            />
            <Button
              variant="contained"
              onClick={handleCreateOrg}
              disabled={createOrgLoading}
            >
              {createOrgLoading ? <CircularProgress size={24} /> : 'Create Organization'}
            </Button>
            {createOrgResponse && (
              <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(createOrgResponse, null, 2)}
                </Typography>
              </Paper>
            )}
            {createOrgError && (
              <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
                <Typography sx={{ color: '#fff' }}>{createOrgError}</Typography>
              </Paper>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Get Organization */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Get Organization</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Organization ID"
              value={getOrgId}
              onChange={(e) => setGetOrgId(e.target.value)}
              fullWidth
            />
            <Button
              variant="contained"
              onClick={handleGetOrg}
              disabled={getOrgLoading}
            >
              {getOrgLoading ? <CircularProgress size={24} /> : 'Get Organization'}
            </Button>
            {getOrgResponse && (
              <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(getOrgResponse, null, 2)}
                </Typography>
              </Paper>
            )}
            {getOrgError && (
              <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
                <Typography sx={{ color: '#fff' }}>{getOrgError}</Typography>
              </Paper>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Update Organization */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Update Organization</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Organization ID"
              value={updateOrgId}
              onChange={(e) => setUpdateOrgId(e.target.value)}
              fullWidth
            />
            <TextField
              label="New Name"
              value={updateOrgName}
              onChange={(e) => setUpdateOrgName(e.target.value)}
              fullWidth
            />
            <Button
              variant="contained"
              onClick={handleUpdateOrg}
              disabled={updateOrgLoading}
            >
              {updateOrgLoading ? <CircularProgress size={24} /> : 'Update Organization'}
            </Button>
            {updateOrgResponse && (
              <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(updateOrgResponse, null, 2)}
                </Typography>
              </Paper>
            )}
            {updateOrgError && (
              <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
                <Typography sx={{ color: '#fff' }}>{updateOrgError}</Typography>
              </Paper>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* List User Organizations */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">List My Organizations</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="contained"
              onClick={handleListUserOrgs}
              disabled={listUserOrgsLoading}
            >
              {listUserOrgsLoading ? <CircularProgress size={24} /> : 'Get My Organizations'}
            </Button>
            {listUserOrgsResponse && (
              <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                <Typography variant="h6" sx={{ mb: 2, color: '#fff' }}>
                  Organizations ({listUserOrgsResponse.organizations?.length || 0}):
                </Typography>
                {listUserOrgsResponse.organizations && listUserOrgsResponse.organizations.length > 0 ? (
                  <List>
                    {listUserOrgsResponse.organizations.map((org: any, index: number) => (
                      <React.Fragment key={org.orgId}>
                        <ListItem>
                          <ListItemText
                            primary={org.name}
                            secondary={`ID: ${org.orgId} | Type: ${org.type}`}
                          />
                        </ListItem>
                        {index < listUserOrgsResponse.organizations.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                ) : (
                  <Typography>No organizations found</Typography>
                )}
              </Paper>
            )}
            {listUserOrgsError && (
              <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
                <Typography sx={{ color: '#fff' }}>{listUserOrgsError}</Typography>
              </Paper>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Transfer Ownership */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Transfer Ownership</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Organization ID"
              value={transferOrgId}
              onChange={(e) => setTransferOrgId(e.target.value)}
              fullWidth
            />
            <TextField
              label="New Owner User ID"
              value={transferNewOwnerId}
              onChange={(e) => setTransferNewOwnerId(e.target.value)}
              fullWidth
            />
            <Button
              variant="contained"
              onClick={handleTransferOwnership}
              disabled={transferLoading}
              color="warning"
            >
              {transferLoading ? <CircularProgress size={24} /> : 'Transfer Ownership'}
            </Button>
            {transferResponse && (
              <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(transferResponse, null, 2)}
                </Typography>
              </Paper>
            )}
            {transferError && (
              <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
                <Typography sx={{ color: '#fff' }}>{transferError}</Typography>
              </Paper>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Upload Org Picture */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Upload Organization Picture</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Organization ID"
              value={uploadPictureOrgId}
              onChange={(e) => setUploadPictureOrgId(e.target.value)}
              fullWidth
            />
            <Button
              variant="outlined"
              component="label"
            >
              Select Image
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => setUploadPictureFile(e.target.files?.[0] || null)}
              />
            </Button>
            {uploadPictureFile && (
              <Typography variant="body2">
                Selected: {uploadPictureFile.name} ({uploadPictureFile.size} bytes)
              </Typography>
            )}
            <Button
              variant="contained"
              onClick={handleUploadOrgPicture}
              disabled={uploadPictureLoading}
            >
              {uploadPictureLoading ? <CircularProgress size={24} /> : 'Upload Picture'}
            </Button>
            {uploadPictureResponse && (
              <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(uploadPictureResponse, null, 2)}
                </Typography>
              </Paper>
            )}
            {uploadPictureError && (
              <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
                <Typography sx={{ color: '#fff' }}>{uploadPictureError}</Typography>
              </Paper>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Get Org Picture */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Get Organization Picture</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Organization ID"
              value={getPictureOrgId}
              onChange={(e) => setGetPictureOrgId(e.target.value)}
              fullWidth
            />
            <Button
              variant="contained"
              onClick={handleGetOrgPicture}
              disabled={getPictureLoading}
            >
              {getPictureLoading ? <CircularProgress size={24} /> : 'Get Picture'}
            </Button>
            {getPictureResponse && (
              <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Organization Picture URL:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                    fontFamily: 'monospace',
                    mb: 2
                  }}
                >
                  {getPictureResponse.profilePictureURL}
                </Typography>
                {getPictureResponse.profilePictureURL && (
                  <Box>
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{ mb: 2 }}
                      onClick={() => window.open(getPictureResponse.profilePictureURL, '_blank')}
                    >
                      Open Image
                    </Button>
                    <Box
                      component="img"
                      src={getPictureResponse.profilePictureURL}
                      alt="Organization"
                      sx={{ maxWidth: '200px', maxHeight: '200px', borderRadius: 1 }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </Box>
                )}
              </Paper>
            )}
            {getPictureError && (
              <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
                <Typography sx={{ color: '#fff' }}>{getPictureError}</Typography>
              </Paper>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Get Org Theme */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Get Organization Theme</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Organization ID"
              value={getThemeOrgId}
              onChange={(e) => setGetThemeOrgId(e.target.value)}
              fullWidth
            />
            <Button
              variant="contained"
              onClick={handleGetOrgTheme}
              disabled={getThemeLoading}
            >
              {getThemeLoading ? <CircularProgress size={24} /> : 'Get Theme'}
            </Button>
            {getThemeResponse && (
              <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(getThemeResponse, null, 2)}
                </Typography>
              </Paper>
            )}
            {getThemeError && (
              <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
                <Typography sx={{ color: '#fff' }}>{getThemeError}</Typography>
              </Paper>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Update Org Theme */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Update Organization Theme</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Organization ID"
              value={updateThemeOrgId}
              onChange={(e) => setUpdateThemeOrgId(e.target.value)}
              fullWidth
            />
            <TextField
              label="Primary Color"
              value={updateThemePrimaryColor}
              onChange={(e) => setUpdateThemePrimaryColor(e.target.value)}
              fullWidth
              placeholder="#1976d2"
            />
            <TextField
              label="Secondary Color"
              value={updateThemeSecondaryColor}
              onChange={(e) => setUpdateThemeSecondaryColor(e.target.value)}
              fullWidth
              placeholder="#dc004e"
            />
            <TextField
              label="Accent Color"
              value={updateThemeAccentColor}
              onChange={(e) => setUpdateThemeAccentColor(e.target.value)}
              fullWidth
              placeholder="#f50057"
            />
            <Button
              variant="contained"
              onClick={handleUpdateOrgTheme}
              disabled={updateThemeLoading}
            >
              {updateThemeLoading ? <CircularProgress size={24} /> : 'Update Theme'}
            </Button>
            {updateThemeResponse && (
              <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(updateThemeResponse, null, 2)}
                </Typography>
              </Paper>
            )}
            {updateThemeError && (
              <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
                <Typography sx={{ color: '#fff' }}>{updateThemeError}</Typography>
              </Paper>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Delete Organization */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Delete Organization</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Organization ID"
              value={deleteOrgId}
              onChange={(e) => setDeleteOrgId(e.target.value)}
              fullWidth
            />
            <Button
              variant="contained"
              onClick={handleDeleteOrg}
              disabled={deleteOrgLoading}
              color="error"
            >
              {deleteOrgLoading ? <CircularProgress size={24} /> : 'Delete Organization'}
            </Button>
            {deleteOrgResponse && (
              <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(deleteOrgResponse, null, 2)}
                </Typography>
              </Paper>
            )}
            {deleteOrgError && (
              <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
                <Typography sx={{ color: '#fff' }}>{deleteOrgError}</Typography>
              </Paper>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default TestOrganizations;
